/**
 * Data transformation utilities for mapping visualization
 * Converts API data to vis-network format
 */

import {
  MappingVisualizationData,
  VisNetworkData,
  VisNetworkNode,
  VisNetworkEdge,
  FilterOptions,
  DataFileWithMappings,
  ColumnMapping,
  SchemaNode,
  DetectedRelationship,
} from '../types/mapping';

/**
 * Get confidence color based on score
 */
export function getConfidenceColor(confidence?: number): string {
  if (!confidence) return '#9ca3af'; // Gray
  if (confidence >= 0.9) return '#10b981'; // Green
  if (confidence >= 0.7) return '#f59e0b'; // Amber
  return '#ef4444'; // Red
}

/**
 * Get confidence label for display
 */
export function getConfidenceLabel(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}

/**
 * Check if column matches filter criteria
 */
function matchesFilter(
  column: ColumnMapping,
  fileId: string,
  filter: FilterOptions
): boolean {
  // File filter
  if (filter.selectedFiles.length > 0 && !filter.selectedFiles.includes(fileId)) {
    return false;
  }

  const mapping = column.acceptedMapping || column.suggestedMapping;
  const isMapped = !!mapping;

  // Confidence filter
  if (mapping && mapping.confidence < filter.minConfidence) {
    return false;
  }

  // Show unmapped filter - when false, hide unmapped columns
  if (!filter.showUnmapped && !isMapped) {
    return false;
  }

  // Show accepted only
  if (filter.showAcceptedOnly && !column.acceptedMapping) {
    return false;
  }

  // Show suggested only - show only columns with suggested mappings
  if (filter.showSuggestedOnly) {
    // If it's suggested only mode and this column doesn't have a mapping, hide it
    if (!isMapped) {
      return false;
    }
  }

  // Show only unmapped - when true, hide mapped columns
  if (filter.showOnlyUnmapped && isMapped) {
    return false;
  }

  // Search term
  if (filter.searchTerm) {
    const term = filter.searchTerm.toLowerCase();
    const columnName = column.columnName.toLowerCase();
    const nodeLabel = mapping?.nodeLabel?.toLowerCase() || '';
    const propertyName = mapping?.propertyName?.toLowerCase() || '';

    if (!columnName.includes(term) && !nodeLabel.includes(term) && !propertyName.includes(term)) {
      return false;
    }
  }

  return true;
}

/**
 * Transform mapping data to vis-network format
 */
export function transformToVisNetwork(
  data: MappingVisualizationData | null,
  filter: FilterOptions
): VisNetworkData {
  const nodes: VisNetworkNode[] = [];
  const edges: VisNetworkEdge[] = [];

  if (!data) {
    return { nodes, edges };
  }

  const { dataFiles, schemaDesign, detectedRelationships } = data;

  // Track which schema nodes and properties are used
  const usedSchemaNodes = new Set<string>();
  const usedProperties = new Set<string>();

  // LEVEL 0: Create file nodes (left side)
  dataFiles.forEach((file) => {
    const mappedCount = file.columns.filter(
      (col) => col.acceptedMapping || col.suggestedMapping
    ).length;

    nodes.push({
      id: `file_${file.fileId}`,
      label: `${file.fileName}\n(${mappedCount}/${file.columnCount} mapped)`,
      group: 'file',
      level: 0,
      title: `File: ${file.fileName}\nSource: ${file.sourceSystem || 'Unknown'}\nRows: ${file.rowCount}\nColumns: ${file.columnCount}`,
      metadata: file,
    });
  });

  // LEVEL 1: Create column nodes and map to properties (level 3)
  dataFiles.forEach((file) => {
    file.columns.forEach((column) => {
      // Apply filters
      if (!matchesFilter(column, file.fileId, filter)) {
        return;
      }

      const columnId = `col_${file.fileId}_${column.columnName}`;
      const mapping = column.acceptedMapping || column.suggestedMapping;

      // Determine column color based on mapping status
      let columnColor = '#f3f4f6'; // Default gray
      let columnBorder = '#6b7280';

      if (!column.include) {
        columnColor = '#e5e7eb'; // Excluded
        columnBorder = '#9ca3af';
      } else if (!mapping) {
        columnColor = '#fef3c7'; // Unmapped (amber tint)
        columnBorder = '#f59e0b';
      } else if (column.acceptedMapping) {
        columnColor = '#d1fae5'; // Accepted (green tint)
        columnBorder = '#10b981';
      }

      // Create column node
      nodes.push({
        id: columnId,
        label: `${column.columnName}\n(${column.dataType})`,
        group: 'column',
        level: 1,
        title: `Column: ${column.columnName}\nType: ${column.dataType}\nQuality: ${column.dataQuality.uniqueness.toFixed(1)}% unique, ${column.dataQuality.nullPercentage.toFixed(1)}% nulls\nSample: ${column.sampleValues.slice(0, 3).join(', ')}`,
        metadata: column,
        color: {
          background: columnColor,
          border: columnBorder,
        },
      });

      // Edge from file to column
      edges.push({
        id: `edge_file_col_${columnId}`,
        from: `file_${file.fileId}`,
        to: columnId,
        color: { color: '#cbd5e1' },
        width: 1,
      });

      // Create mapping edge if mapping exists
      if (mapping) {
        const targetPropertyId = `schema_${mapping.nodeLabel}_${mapping.propertyName}`;
        usedSchemaNodes.add(mapping.nodeLabel);
        usedProperties.add(targetPropertyId);

        const isAccepted = !!column.acceptedMapping;
        const confidence = mapping.confidence;

        edges.push({
          id: `edge_mapping_${columnId}`,
          from: columnId,
          to: targetPropertyId,
          label: getConfidenceLabel(confidence),
          title: `Mapping: ${column.columnName} → ${mapping.nodeLabel}.${mapping.propertyName}\nConfidence: ${getConfidenceLabel(confidence)}\nReasoning: ${mapping.reasoning}\nStatus: ${isAccepted ? 'Accepted' : 'Suggested'}`,
          dashes: !isAccepted,
          width: isAccepted ? 2 : 1,
          color: {
            color: getConfidenceColor(confidence),
            opacity: isAccepted ? 1 : 0.6,
          },
          arrows: {
            to: { enabled: true, scaleFactor: 0.5 },
          },
          metadata: { mapping, column, isAccepted },
        });
      }
    });
  });

  // LEVEL 2 & 3: Create schema nodes and properties (right side)
  if (schemaDesign) {
    console.log('[MappingTransformer] Processing schema design with', schemaDesign.nodes?.length, 'nodes');
    schemaDesign.nodes.forEach((node) => {
      const nodeId = `schema_${node.label}`;
      const isUsed = usedSchemaNodes.has(node.label);

      console.log('[MappingTransformer] Creating schema node:', node.label, 'isUsed:', isUsed);

      // Create schema node (LEVEL 3 - after properties)
      nodes.push({
        id: nodeId,
        label: node.label,
        group: 'schemaNode',
        level: 3,
        title: `Node: ${node.label}\nCategory: ${node.category || 'N/A'}\n${node.description}`,
        metadata: node,
        color: {
          background: isUsed ? '#10b981' : '#d1d5db',
          border: isUsed ? '#059669' : '#9ca3af',
        },
      });

      // Create property nodes (LEVEL 2 - before schema nodes)
      if (node.properties) {
        node.properties.forEach((prop) => {
          const propId = `schema_${node.label}_${prop.name}`;
          const isPropUsed = usedProperties.has(propId);

          nodes.push({
            id: propId,
            label: `${prop.name}: ${prop.type}`,
            group: 'schemaProperty',
            level: 2,
            title: `Property: ${prop.name}\nType: ${prop.type}\nRequired: ${prop.required ? 'Yes' : 'No'}\nUnique: ${prop.unique ? 'Yes' : 'No'}\nIndexed: ${prop.indexed ? 'Yes' : 'No'}`,
            metadata: { node, property: prop },
            color: {
              background: isPropUsed ? '#d1fae5' : '#f3f4f6',
              border: isPropUsed ? '#10b981' : '#9ca3af',
            },
            font: {
              size: 12,
            },
          });

          // Edge from property to schema node (reversed direction)
          edges.push({
            id: `edge_prop_node_${propId}`,
            from: propId,
            to: nodeId,
            color: { color: '#d1d5db' },
            width: 1,
            title: `${prop.name} → ${node.label}`,
          });
        });
      }
    });
  }

  // Add relationship nodes and edges if enabled
  if (filter.showRelationships && detectedRelationships && schemaDesign) {
    detectedRelationships.forEach((rel, index) => {
      const relId = `rel_${index}_${rel.relationshipType}`;

      // Create relationship node (intermediate node between schema nodes)
      nodes.push({
        id: relId,
        label: rel.relationshipType,
        group: 'relationship',
        level: 2.5, // Between schema nodes and properties
        title: `Relationship: ${rel.fromNode} -[:${rel.relationshipType}]-> ${rel.toNode}\n${rel.reasoning}\nVia: ${rel.sourceFile.fileName}.${rel.sourceFile.column} = ${rel.targetFile.fileName}.${rel.targetFile.column}`,
        metadata: rel,
        shape: 'diamond',
        color: {
          background: '#8b5cf6',
          border: '#6d28d9',
        },
        font: {
          color: '#ffffff',
          size: 12,
        },
      });

      // Edge from source column to relationship
      const sourceColId = `col_${rel.sourceFile.fileId}_${rel.sourceFile.column}`;
      edges.push({
        id: `edge_rel_from_${relId}`,
        from: sourceColId,
        to: relId,
        color: { color: '#8b5cf6', opacity: 0.8 },
        width: 2,
        dashes: true,
        label: 'FK',
        title: `Foreign Key: ${rel.sourceFile.column} references ${rel.targetFile.column}`,
      });

      // Edge from relationship to target node
      const targetNodeId = `schema_${rel.toNode}`;
      edges.push({
        id: `edge_rel_to_${relId}`,
        from: relId,
        to: targetNodeId,
        color: { color: '#8b5cf6' },
        width: 2,
        arrows: {
          to: { enabled: true, scaleFactor: 0.8 },
        },
      });
    });
  }

  console.log('[MappingTransformer] Final counts - Nodes:', nodes.length, 'Edges:', edges.length);
  console.log('[MappingTransformer] Node groups:', {
    files: nodes.filter(n => n.group === 'file').length,
    columns: nodes.filter(n => n.group === 'column').length,
    schemaNodes: nodes.filter(n => n.group === 'schemaNode').length,
    schemaProperties: nodes.filter(n => n.group === 'schemaProperty').length,
    relationships: nodes.filter(n => n.group === 'relationship').length,
  });

  return { nodes, edges };
}

/**
 * Get statistics about mappings
 */
export function getMappingStats(data: MappingVisualizationData | null): {
  totalFiles: number;
  totalColumns: number;
  mappedColumns: number;
  acceptedColumns: number;
  unmappedColumns: number;
  highConfidenceColumns: number;
  detectedRelationships: number;
} {
  if (!data) {
    return {
      totalFiles: 0,
      totalColumns: 0,
      mappedColumns: 0,
      acceptedColumns: 0,
      unmappedColumns: 0,
      highConfidenceColumns: 0,
      detectedRelationships: 0,
    };
  }

  let totalColumns = 0;
  let mappedColumns = 0;
  let acceptedColumns = 0;
  let highConfidenceColumns = 0;

  data.dataFiles.forEach((file) => {
    file.columns.forEach((col) => {
      totalColumns++;
      const mapping = col.acceptedMapping || col.suggestedMapping;
      if (mapping) {
        mappedColumns++;
        if (col.acceptedMapping) {
          acceptedColumns++;
        }
        if (mapping.confidence >= 0.9) {
          highConfidenceColumns++;
        }
      }
    });
  });

  return {
    totalFiles: data.dataFiles.length,
    totalColumns,
    mappedColumns,
    acceptedColumns,
    unmappedColumns: totalColumns - mappedColumns,
    highConfidenceColumns,
    detectedRelationships: data.detectedRelationships?.length || 0,
  };
}

/**
 * Check if all files are fully mapped
 */
export function isFullyMapped(data: MappingVisualizationData | null): boolean {
  if (!data || data.dataFiles.length === 0) {
    return false;
  }

  return data.dataFiles.every((file) =>
    file.columns.every(
      (col) => !col.include || col.acceptedMapping || col.suggestedMapping
    )
  );
}

/**
 * Check if all mappings are accepted
 */
export function allMappingsAccepted(data: MappingVisualizationData | null): boolean {
  if (!data || data.dataFiles.length === 0) {
    return false;
  }

  return data.dataFiles.every((file) =>
    file.columns.every((col) => !col.include || col.acceptedMapping)
  );
}
